import * as React from "react";
import { isEmpty, isEqual } from "lodash";

const ProblemContext = React.createContext();

function problemReducer(state, action) {
  switch (action.type) {
    case "load problem": {
      return {
        ...state,
        varriables: action.payload.varriables,
        criteria_rates: action.payload.criteria_rates,
        alternatives: action.payload.alternatives,
        problemType: action.payload.problemType,
        user_preferences: action.payload.user_preferences,
        template_id: action.payload.template_id,
        copyAnswersFromTemplate: action.payload.copy_answers,
        dirty_varriables: false,
        status: "create",
      };
    }
    case "choose problem type": {
      return {
        ...state,
        problemType: action.payload.problemType,
        status: "create",
      };
    }
    case "create problem": {
      return {
        ...state,
        uid: action.payload.uid,
        problemName: action.payload.problemName,
        varriables: action.payload.varriables,
        question_pairs: action.payload.question_pairs,
        criteria_rates: action.payload.criteria_rates,
        category: action.payload.category,
        photo: action.payload.photoUrl,
        weights: action.payload.weights,
        user_preferences: [],
        progress: [0],
        status: "alternatives",
        dirty_varriables: action.payload.dirty_varriables,
      };
    }
    case "update alternatives": {
      return {
        ...state,
        alternatives: action.payload.alternatives,
        weights:
          state.problemType === "saw" ? action.payload.weights : state.weights,
        status: "questions",
      };
    }
    case "update problem": {
      return {
        ...state,
        question_pairs: action.payload.question_pairs,
        user_preferences: action.payload.user_preferences,
        progress:
          action.payload.progress === 0 && !isEmpty(state.progress)
            ? [...state.progress]
            : [...state.progress, action.payload.progress],
        status: "questions",
      };
    }
    case "undo question": {
      let up = [...state.user_preferences];
      up.pop();
      let qp = [...state.question_pairs];
      qp.pop();
      let p = [...state.progress];
      p.pop();
      return {
        ...state,
        question_pairs: qp,
        user_preferences: up,
        progress: p,
      };
    }
    case "first question": {
      let up = [];
      let qp = [state.question_pairs[0]];
      return {
        ...state,
        question_pairs: qp,
        user_preferences: up,
        progress: [0],
      };
    }
    case "finished problem": {
      return {
        ...state,
        progress: [...state.progress, action.payload.progress],
        status: "finished",
      };
    }
    case "update answers": {
      return {
        ...state,
        answers: action.payload.answers,
        accuracy: action.payload.accuracy,
        user_preferences: action.payload.user_preferences,
        status: "finished",
      };
    }
    case "truncate preferences": {
      return {
        ...state,
        user_preferences: [],
      };
    }
    case "reset": {
      return {
        uid: "",
        problemName: "",
        problemType: "saw",
        varriables: {},
        status: "type",
        user_preferences: [],
        answers: null,
        question_pairs: null,
        criteria_rates: null,
        alternatives: {},
        category: "private",
        weights: [
          [2, true],
          [2, true],
        ],
        progress: [0],
        accuracy: 0,
        error: null,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function ProblemProvider({ children }) {
  const [state, dispatch] = React.useReducer(problemReducer, {
    uid: "",
    problemName: "",
    problemType: "saw",
    varriables: {},
    status: "type",
    user_preferences: [],
    answers: null,
    question_pairs: null,
    criteria_rates: null,
    alternatives: {},
    category: "private",
    weights: [
      [2, true, false],
      [2, true, false],
    ],
    progress: [0],
    accuracy: 0,
    error: null,
    copyAnswersFromTemplate: false,
    dirty_varriables: false,
  });
  const value = React.useMemo(() => [state, dispatch], [state]);
  return (
    <ProblemContext.Provider value={value}>{children}</ProblemContext.Provider>
  );
}

function useProblem() {
  const context = React.useContext(ProblemContext);
  if (context === undefined) {
    throw new Error("useProblem must be used within a ProblemProvider");
  }
  return context;
}

export { ProblemProvider, useProblem };
